import { useFrame } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import avatarLipsyncConfig from '../config/avatarLipsyncConfig';
import {
  AVATURN_ANIMATED_FACE_MORPH_NAMES,
  SPEECH_SAFE_MOUTH_PRESERVE_NAMES
} from '../constants/emotionBlendshapes';
import {
  VISEME15_CONFLICT_TARGETS,
  VISEME15_MOUTH_OPEN_TARGET,
  VISEME15_OPEN_VOWELS,
  VISEME15_RESET_TARGETS
} from '../constants/viseme15Blendshapes';
import {
  applyVisemeBlendshape,
  computeMouthOpenness,
  getFacialFrameRate,
  getInterpolatedFrameIndex,
  interpolateViseme15Frame,
  refineViseme15Entries,
  resetMorphTargets,
  smoothVisemeEntries
} from '../helpers/lipsync';

const { viseme15: viseme15Config } = avatarLipsyncConfig;

const LIPSYNC_PRESERVE_NAMES = [
  ...AVATURN_ANIMATED_FACE_MORPH_NAMES,
  ...SPEECH_SAFE_MOUTH_PRESERVE_NAMES
];
const VOWEL_NAMES = new Set(VISEME15_OPEN_VOWELS);

/**
 * Viseme15 lipsync for Nudgyt Agent clients (15-coefficient wire format).
 * Accepts indexed float arrays or per-frame ARKit blendshape objects.
 */
export const useModelViseme15Lipsync = ({ client, scene, enabled = true }) => {
  const smoothedWeightsRef = useRef({});
  const smoothedMouthOpenRef = useRef(0);
  const wasTalkingRef = useRef(false);

  useFrame(() => {
    if (!enabled || !client?.isTalking) return;
    if (!client?.facialData || !client?.audioRef?.current) return;

    const currentTime = client.audioRef.current.currentTime;
    if (currentTime === 0) return;

    const { prevFrame, nextFrame, lerpFactor } = getInterpolatedFrameIndex(
      client.facialData,
      currentTime,
      getFacialFrameRate(client)
    );

    if (!prevFrame || !nextFrame) return;

    const rawEntries = interpolateViseme15Frame(prevFrame, nextFrame, lerpFactor);
    if (!rawEntries?.length) return;

    const refinedEntries = refineViseme15Entries(rawEntries);
    const { entries: blendshapeEntries, nextWeights } = smoothVisemeEntries(
      refinedEntries,
      smoothedWeightsRef.current
    );
    smoothedWeightsRef.current = nextWeights;

    if (!blendshapeEntries.length) return;

    const activeNames = new Set(blendshapeEntries.map(([name]) => name));

    VISEME15_RESET_TARGETS.forEach((name) => {
      if (!activeNames.has(name)) {
        applyVisemeBlendshape({
          targetBlendShape: name,
          value: 0,
          speed: viseme15Config.decaySpeed,
          scene
        });
      }
    });

    VISEME15_CONFLICT_TARGETS.forEach((name) => {
      applyVisemeBlendshape({
        targetBlendShape: name,
        value: 0,
        speed: viseme15Config.decaySpeed,
        scene
      });
    });

    const targetMouthOpen = computeMouthOpenness(blendshapeEntries);
    smoothedMouthOpenRef.current +=
      (targetMouthOpen - smoothedMouthOpenRef.current) *
      viseme15Config.mouthOpenSmoothAlpha;

    applyVisemeBlendshape({
      targetBlendShape: VISEME15_MOUTH_OPEN_TARGET,
      value: smoothedMouthOpenRef.current,
      speed: viseme15Config.mouthOpenApplySpeed,
      scene
    });

    blendshapeEntries.forEach(([blendName, value]) => {
      applyVisemeBlendshape({
        targetBlendShape: blendName,
        value,
        speed: VOWEL_NAMES.has(blendName)
          ? viseme15Config.vowelApplySpeed
          : viseme15Config.applySpeed,
        scene
      });
    });
  });

  useEffect(() => {
    if (!enabled || !scene) return;

    const isTalking = !!client?.isTalking;
    const justStoppedTalking = wasTalkingRef.current && !isTalking;

    if (justStoppedTalking) {
      smoothedWeightsRef.current = {};
      smoothedMouthOpenRef.current = 0;
      resetMorphTargets(
        scene,
        [
          ...VISEME15_RESET_TARGETS,
          ...VISEME15_CONFLICT_TARGETS,
          VISEME15_MOUTH_OPEN_TARGET
        ],
        {
          preserveNames: LIPSYNC_PRESERVE_NAMES,
          lerpSpeed: viseme15Config.resetLerpSpeed
        }
      );
    }

    wasTalkingRef.current = isTalking;
  }, [client?.isTalking, enabled, scene]);
};
